import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { electronApis } from '@/features/electron-api';

import { SettingsSection } from '../settings-section';

import type { LocalTranscriptionReadiness } from '@electron/shared/meetings.types';

const DICTATION_READINESS_QUERY_KEY = ['dictation-readiness'] as const;

function describe(readiness: LocalTranscriptionReadiness): string {
  switch (readiness.state) {
    case 'not_started':
      return 'Not downloaded yet. Dictation stays unavailable until it is.';
    case 'preparing': {
      const fraction = readiness.progress?.fraction;
      const percent =
        typeof fraction === 'number' && Number.isFinite(fraction)
          ? ` ${Math.round(fraction * 100)}%`
          : '';
      return readiness.phase === 'downloading'
        ? `Downloading${percent}. This only happens once.`
        : 'Loading into memory. Almost there.';
    }
    case 'cache_ready':
    case 'disk_cached':
      return 'Downloaded. It loads the first time you dictate.';
    case 'resident_ready':
    case 'ready':
      return 'Ready. Dictation will start straight away.';
    case 'failed':
      return `Preparation failed: ${readiness.message}`;
    case 'unavailable':
      return `Unavailable on this Mac: ${readiness.message}`;
  }
}

export const DictationReadiness = () => {
  const queryClient = useQueryClient();

  const { data: readiness } = useQuery({
    queryKey: DICTATION_READINESS_QUERY_KEY,
    queryFn: () => electronApis.meetings.getLocalTranscriptionReadiness(),
    retry: false,
  });

  useEffect(
    () =>
      electronApis.meetings.listenLocalTranscriptionReadinessChanged((next) => {
        queryClient.setQueryData(DICTATION_READINESS_QUERY_KEY, next);
      }),
    [queryClient]
  );

  const retry = useMutation({
    mutationFn: () => electronApis.meetings.retryLocalTranscriptionReadiness(),
    onSuccess: (next) => {
      queryClient.setQueryData(DICTATION_READINESS_QUERY_KEY, next);
    },
    onError: () => toast.error('Failed to retry speech model preparation'),
  });

  if (!readiness) return null;

  return (
    <SettingsSection.Item>
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Speech model</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          {describe(readiness)}
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      {readiness.state === 'failed' && readiness.retryable && (
        <Button
          variant="ghost"
          size="xs"
          onClick={() => retry.mutate()}
          isLoading={retry.isPending}
        >
          Retry
        </Button>
      )}
    </SettingsSection.Item>
  );
};
